import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import ReportPaginator from '../components/ReportPaginator';
import ScreenHeader from '../components/layout/ScreenHeader';
import { Button as CmButton, FilterCard, FilterToggle, StatCard, StatGrid } from '../ui';
import { ClipboardCheck, LogIn, Timer, UsersRound } from 'lucide-react';
import { Activity, Building2, CalendarDays, ChevronDown, ChevronUp, RefreshCw, UserRound } from 'lucide-react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    IconButton,
    Chip,
    Avatar,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tooltip,
    Collapse,
} from '@mui/material';
import {
    Assignment as AuditIcon,
    TableRestaurant as TableIcon,
    AccessTime as AccessTimeIcon,
    Login as LoginIcon,
    Logout as LogoutIcon,
    Circle as CircleIcon,
    Groups as GroupsIcon,
    Timer as TimerIcon,
    SwapHoriz as TransferIcon,
    EventSeat as SeatIcon,
    PersonRemove as PersonRemoveIcon,
    MonetizationOn as MoneyIcon,
    PlayCircle as SessionIcon,
    MeetingRoom as OpenTableIcon,
    Notes as NoteIcon,
} from '@mui/icons-material';

let API_URL = process.env.REACT_APP_API_URL || 'https://localhost:5000';
if (API_URL.endsWith('/api')) {
    API_URL = API_URL.slice(0, -4);
}
if (API_URL.endsWith('/')) {
    API_URL = API_URL.slice(0, -1);
}

const DealerAudit = () => {
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const [loading, setLoading] = useState(true);
    const [auditData, setAuditData] = useState(null);
    const [expandedRows, setExpandedRows] = useState({});
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const [filters, setFilters] = useState({
        dealerId: '',
        clubId: '',
        status: '',
        startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        page: 1,
        limit: 20,
    });

    const fetchAuditLog = useCallback(async () => {
        try {
            setLoading(true);
            const config = {
                headers: { Authorization: `Bearer ${userInfo?.token}` },
            };

            const params = new URLSearchParams();
            if (filters.dealerId) params.append('dealerId', filters.dealerId);
            if (filters.clubId) params.append('clubId', filters.clubId);
            if (filters.status) params.append('status', filters.status);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            params.append('page', filters.page);
            params.append('limit', filters.limit);

            const { data } = await axios.get(`${API_URL}/api/dealer/audit-log?${params.toString()}`, config);
            setAuditData(data.data);
            setLastUpdatedAt(new Date());
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to load dealer audit log');
        } finally {
            setLoading(false);
        }
    }, [userInfo, filters]);

    useEffect(() => {
        fetchAuditLog();
        // Auto-refresh every 30 seconds so live (active) durations stay current
        const interval = setInterval(fetchAuditLog, 30000);
        return () => clearInterval(interval);
    }, [fetchAuditLog]);

    const toggleRow = (id) => {
        setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const formatDuration = (minutes) => {
        const h = Math.floor((minutes || 0) / 60);
        const m = (minutes || 0) % 60;
        return `${h}h ${m}m`;
    };

    // Table names aren't guaranteed unique (e.g. two tables both named "A" in different
    // areas), so always include the table number too - otherwise two different tables
    // render as identical, indistinguishable chips.
    const formatTableLabel = (t) => {
        if (t.tableName && t.tableNumber && t.tableName !== t.tableNumber) {
            return `${t.tableName} · ${t.tableNumber}`;
        }
        return t.tableName || t.tableNumber || 'Unnamed table';
    };

    // One line per thing the dealer did, e.g. "Seated Ravi at seat 3"
    const describeAction = (a) => {
        const d = a.details || {};
        const player = d.playerName || 'player';
        const seat = a.seatNumber ? ` at seat ${a.seatNumber}` : '';
        switch (a.actionType) {
            case 'seat_player':
                return { icon: SeatIcon, color: 'var(--cm-success)', text: `Seated ${player}${seat}` };
            case 'remove_player':
                return { icon: PersonRemoveIcon, color: 'var(--cm-danger)', text: `Removed ${player}${a.seatNumber ? ` from seat ${a.seatNumber}` : ''}${d.reason ? ` (${d.reason})` : ''}` };
            case 'rebuy_player':
                return { icon: MoneyIcon, color: 'var(--cm-warning)', text: `Added ₹${d.amount ?? 0} chips for ${player}` };
            case 'control_session': {
                const verb = { start: 'Started', pause: 'Paused', resume: 'Resumed', stop: 'Stopped' }[d.action] || 'Updated';
                return { icon: SessionIcon, color: 'var(--cm-info)', text: `${verb} session for ${player}` };
            }
            case 'open_table':
                return { icon: OpenTableIcon, color: 'var(--cm-info)', text: 'Opened table' };
            case 'transfer_out':
                return { icon: TransferIcon, color: 'var(--cm-gold-text)', text: `Transferred table to ${d.toDealerName || 'another dealer'}${d.occupiedSeats ? ` (${d.occupiedSeats} players seated)` : ''}` };
            case 'transfer_in':
                return { icon: TransferIcon, color: 'var(--cm-gold-text)', text: `Received table from ${d.fromDealerName || 'another dealer'}` };
            default:
                return { icon: NoteIcon, color: 'var(--cm-text-muted)', text: a.actionType.replace(/_/g, ' ') };
        }
    };

    const actionTableLabel = (a) => {
        if (a.table) return formatTableLabel(a.table);
        if (a.details?.tableName) return formatTableLabel(a.details);
        return null;
    };

    const statusColor = (status) => {
        switch (status) {
            case 'active':
                return { bg: 'var(--cm-success-soft)', color: 'var(--cm-success)' };
            case 'completed':
                return { bg: 'var(--cm-info-soft)', color: 'var(--cm-info)' };
            case 'auto-ended':
                return { bg: 'var(--cm-warning-soft)', color: 'var(--cm-warning)' };
            case 'abandoned':
                return { bg: 'var(--cm-danger-soft)', color: 'var(--cm-danger)' };
            default:
                return { bg: 'var(--cm-surface-2)', color: 'var(--cm-text-muted)' };
        }
    };

    if (loading && !auditData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const STATUS_LABELS = { active: 'Active', completed: 'Completed', 'auto-ended': 'Auto-Ended', abandoned: 'Abandoned' };
    const filterDealer = auditData?.dealers?.find((d) => d._id === filters.dealerId);
    const filterClub = auditData?.clubs?.find((c) => c._id === filters.clubId);
    const filterChips = [
        filters.dealerId && { icon: UserRound, label: filterDealer?.name || 'Dealer' },
        filters.clubId && { icon: Building2, label: filterClub ? `${filterClub.name} (${filterClub.clubCode})` : 'Club' },
        filters.status && { icon: Activity, label: STATUS_LABELS[filters.status] || filters.status },
        (filters.startDate || filters.endDate) && { icon: CalendarDays, label: `${filters.startDate || '…'} → ${filters.endDate || '…'}` },
    ].filter(Boolean);

    return (
        <Box>
            <ScreenHeader
                title="Dealer audit"
                description="When each dealer logged in and out, which tables they ran, and for how long."
                actions={
                    <>
                        <span className="text-xs text-cm-muted tabular-nums">
                            Updated {lastUpdatedAt ? moment(lastUpdatedAt).format('hh:mm:ss A') : '—'}
                        </span>
                        <FilterToggle
                            open={filtersOpen}
                            onToggle={() => setFiltersOpen((o) => !o)}
                            count={filterChips.length}
                            controls="dealer-audit-filters"
                        />
                        <CmButton variant="secondary" icon={RefreshCw} onClick={fetchAuditLog}>Refresh</CmButton>
                    </>
                }
            />

            <StatGrid className="mb-6">
                <StatCard label="Dealer sessions" icon={ClipboardCheck} value={auditData?.summary?.totalSessions || 0} hint="In the selected range" />
                <StatCard label="Logged in now" icon={LogIn} tone="success" value={auditData?.summary?.activeNow || 0} />
                <StatCard label="Dealers involved" icon={UsersRound} value={auditData?.summary?.totalDealers || 0} />
                <StatCard label="Hours logged" icon={Timer} value={`${auditData?.summary?.totalHours || 0}h`} />
            </StatGrid>

            {/* Filters */}
            <FilterCard
                id="dealer-audit-filters"
                className="mb-6"
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                chips={filterChips}
                emptyLabel="All dealers, clubs and statuses"
            >
                <div className="flex flex-wrap gap-4 items-center">
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Filter by Dealer</InputLabel>
                        <Select
                            value={filters.dealerId}
                            label="Filter by Dealer"
                            onChange={(e) => setFilters((prev) => ({ ...prev, dealerId: e.target.value, page: 1 }))}
                        >
                            <MenuItem value="">All Dealers</MenuItem>
                            {auditData?.dealers?.map((d) => (
                                <MenuItem key={d._id} value={d._id}>
                                    {d.name} {d.identifier ? `(${d.identifier})` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Filter by Club</InputLabel>
                        <Select
                            value={filters.clubId}
                            label="Filter by Club"
                            onChange={(e) => setFilters((prev) => ({ ...prev, clubId: e.target.value, page: 1 }))}
                        >
                            <MenuItem value="">All Clubs</MenuItem>
                            {auditData?.clubs?.map((c) => (
                                <MenuItem key={c._id} value={c._id}>
                                    {c.name} ({c.clubCode})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={filters.status}
                            label="Status"
                            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value, page: 1 }))}
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="auto-ended">Auto-Ended</MenuItem>
                            <MenuItem value="abandoned">Abandoned</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        type="date"
                        label="Start Date"
                        value={filters.startDate}
                        onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value, page: 1 }))}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ minWidth: 150 }}
                    />
                    <TextField
                        type="date"
                        label="End Date"
                        value={filters.endDate}
                        onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value, page: 1 }))}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ minWidth: 150 }}
                    />
                </div>
            </FilterCard>

            {/* Sessions Table */}
            <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: 'var(--cm-surface-2)' }}>
                                <TableRow>
                                    <TableCell />
                                    <TableCell><strong>S.No.</strong></TableCell>
                                    <TableCell><strong>Dealer</strong></TableCell>
                                    <TableCell><strong>Club</strong></TableCell>
                                    <TableCell><strong>Tables Managed</strong></TableCell>
                                    <TableCell><strong>Login (Session Start)</strong></TableCell>
                                    <TableCell><strong>Logout (Session End)</strong></TableCell>
                                    <TableCell><strong>Duration</strong></TableCell>
                                    <TableCell><strong>Activity</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {auditData?.sessions?.map((session, index) => {
                                    const sc = statusColor(session.status);
                                    const isExpanded = !!expandedRows[session._id];
                                    const tables = session.tables || [];
                                    const actions = session.actions || [];
                                    const canExpand = tables.length > 1 || actions.length > 0;
                                    const serialNumber = (filters.page - 1) * filters.limit + index + 1;
                                    return (
                                        <React.Fragment key={session._id}>
                                            <TableRow hover sx={{ bgcolor: session.status === 'active' ? 'var(--cm-success-soft)' : 'inherit' }}>
                                                <TableCell>
                                                    {canExpand && (
                                                        <IconButton size="small" onClick={() => toggleRow(session._id)}>
                                                            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                                <TableCell>{serialNumber}</TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'var(--cm-red)' }}>
                                                            {session.dealer?.name?.[0]?.toUpperCase() || 'D'}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                {session.dealer?.name || 'Unknown Dealer'}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {session.dealer?.identifier || session.dealer?.phoneNumber || '-'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{session.club?.name || 'N/A'}</Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {session.club?.clubCode}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {tables.length === 0 ? (
                                                        <Typography variant="body2" color="textSecondary">No table assigned</Typography>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 260 }}>
                                                            {(isExpanded ? tables : tables.slice(0, 2)).map((t) => (
                                                                <Chip
                                                                    key={t._id}
                                                                    size="small"
                                                                    icon={<TableIcon sx={{ fontSize: 14 }} />}
                                                                    label={formatTableLabel(t)}
                                                                    sx={{ bgcolor: 'var(--cm-info-soft)', color: 'var(--cm-info)' }}
                                                                />
                                                            ))}
                                                            {!isExpanded && tables.length > 2 && (
                                                                <Chip
                                                                    size="small"
                                                                    label={`+${tables.length - 2} more`}
                                                                    onClick={() => toggleRow(session._id)}
                                                                    sx={{ cursor: 'pointer' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <LoginIcon sx={{ fontSize: 16, color: 'var(--cm-success)' }} />
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {moment(session.sessionStart).format('hh:mm A')}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {moment(session.sessionStart).format('DD MMM YYYY')}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {session.sessionEnd ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <LogoutIcon sx={{ fontSize: 16, color: 'var(--cm-danger)' }} />
                                                            <Box>
                                                                <Typography variant="body2">
                                                                    {moment(session.sessionEnd).format('hh:mm A')}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {moment(session.sessionEnd).format('DD MMM YYYY')}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Chip
                                                            size="small"
                                                            icon={<CircleIcon sx={{ fontSize: '10px !important' }} />}
                                                            label="Still logged in"
                                                            sx={{ bgcolor: 'var(--cm-success-soft)', color: 'var(--cm-success)' }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={formatDuration(session.durationMinutes)}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: session.status === 'active' ? 'var(--cm-success-soft)' : 'var(--cm-surface-2)',
                                                            color: session.status === 'active' ? 'var(--cm-success)' : 'var(--cm-text-muted)',
                                                            fontWeight: 'bold',
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {actions.length === 0 ? (
                                                        <Typography variant="body2" color="textSecondary">No actions</Typography>
                                                    ) : (
                                                        <Chip
                                                            size="small"
                                                            label={`${actions.length} action${actions.length === 1 ? '' : 's'}`}
                                                            onClick={() => toggleRow(session._id)}
                                                            sx={{ bgcolor: 'var(--cm-gold-soft)', color: 'var(--cm-gold-text)', fontWeight: 'bold', cursor: 'pointer' }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={session.status}
                                                        size="small"
                                                        sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 'bold', textTransform: 'capitalize' }}
                                                    />
                                                    {session.endedBy && (
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            by {session.endedBy}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {canExpand && (
                                                <TableRow>
                                                    <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                                                        <Collapse in={isExpanded}>
                                                            <Box sx={{ p: 2, bgcolor: 'var(--cm-surface-2)' }}>
                                                                {tables.length > 1 && (
                                                                    <>
                                                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                                            All tables managed in this session ({tables.length}):
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, mb: actions.length ? 2 : 0 }}>
                                                                            {tables.map((t) => (
                                                                                <Chip
                                                                                    key={t._id}
                                                                                    size="small"
                                                                                    icon={<TableIcon sx={{ fontSize: 14 }} />}
                                                                                    label={formatTableLabel(t)}
                                                                                    variant="outlined"
                                                                                />
                                                                            ))}
                                                                        </Box>
                                                                    </>
                                                                )}
                                                                {actions.length > 0 && (
                                                                    <>
                                                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                                            What the dealer did ({actions.length}):
                                                                        </Typography>
                                                                        <Box sx={{ mt: 1, maxHeight: 320, overflowY: 'auto' }}>
                                                                            {actions.map((a) => {
                                                                                const { icon: Icon, color, text } = describeAction(a);
                                                                                const tableLabel = actionTableLabel(a);
                                                                                return (
                                                                                    <Box
                                                                                        key={a._id}
                                                                                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: '1px solid var(--cm-border)' }}
                                                                                    >
                                                                                        <Typography variant="caption" color="textSecondary" sx={{ minWidth: 72 }}>
                                                                                            {moment(a.timestamp).format('hh:mm A')}
                                                                                        </Typography>
                                                                                        <Icon sx={{ fontSize: 18, color }} />
                                                                                        <Typography variant="body2" sx={{ flex: 1 }}>{text}</Typography>
                                                                                        {tableLabel && (
                                                                                            <Chip
                                                                                                size="small"
                                                                                                icon={<TableIcon sx={{ fontSize: 14 }} />}
                                                                                                label={tableLabel}
                                                                                                variant="outlined"
                                                                                            />
                                                                                        )}
                                                                                    </Box>
                                                                                );
                                                                            })}
                                                                        </Box>
                                                                    </>
                                                                )}
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                {(!auditData?.sessions || auditData.sessions.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                                            <AuditIcon sx={{ fontSize: 50, color: 'gray', mb: 1 }} />
                                            <Typography color="textSecondary">
                                                No dealer sessions found for the selected filters
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {auditData?.pagination && (
                        <ReportPaginator
                            first={(filters.page - 1) * filters.limit}
                            rows={filters.limit}
                            totalRecords={auditData.pagination.total || 0}
                            rowsPerPageOptions={[10, 20, 50, 100]}
                            onPageChange={(event) =>
                                setFilters((prev) => ({ ...prev, page: event.page + 1, limit: event.rows }))
                            }
                        />
                    )}
            </Paper>
        </Box>
    );
};

export default DealerAudit;
